/**
 * The application toolbar.
 *
 * Compact by design: the primary actions stay visible, everything else folds
 * into an overflow menu. On narrow screens almost everything folds, because a
 * row of 14 icons on a phone is a row of 14 mistakes waiting to happen.
 */
import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { BRANDING } from '../config/branding';
import type { Translator } from '../i18n';
import type { PanelLayout, SaveStatus, ThemePreference } from '../persistence';
import {
  Columns,
  Code,
  Download,
  FolderOpen,
  HelpCircle,
  Layout,
  Monitor,
  Moon,
  MoreHorizontal,
  Plus,
  Redo,
  Settings,
  Sparkles,
  Sun,
  Undo,
  Upload,
  Wand,
  Workflow,
} from './ui/icons';
import { IconButton, Menu, Tooltip } from './ui';
import type { MenuItem } from './ui';

export interface ToolbarProps {
  readonly projectName: string;
  readonly onProjectNameChange: (name: string) => void;
  readonly saveStatus: SaveStatus;
  readonly theme: ThemePreference;
  readonly onThemeChange: (theme: ThemePreference) => void;
  readonly panelLayout: PanelLayout;
  readonly onPanelLayoutChange: (layout: PanelLayout) => void;
  readonly onNew: () => void;
  readonly onOpenProjects: () => void;
  readonly onOpenExamples: () => void;
  readonly onImportFile: (file: File) => void;
  readonly onExport: () => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly onFormat: () => void;
  readonly onRelayout: () => void;
  readonly onOpenSettings: () => void;
  readonly onOpenHelp: () => void;
  readonly onSwapPanels: () => void;
  readonly compact: boolean;
  readonly t: Translator['t'];
}

const THEME_ICON = { system: Monitor, light: Sun, dark: Moon } as const;

export function Toolbar(props: ToolbarProps) {
  const { t, compact } = props;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [editingName, setEditingName] = useState(false);

  const ThemeIcon = THEME_ICON[props.theme];
  const nextTheme: ThemePreference =
    props.theme === 'system' ? 'light' : props.theme === 'light' ? 'dark' : 'system';

  const pickFile = (): void => fileInputRef.current?.click();

  const onFileChosen = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) props.onImportFile(file);
    // Reset so choosing the same file twice still fires a change event.
    event.target.value = '';
  };

  const overflowItems: MenuItem[] = [
    {
      id: 'examples',
      label: t('toolbar.examples'),
      icon: <Sparkles />,
      onSelect: props.onOpenExamples,
    },
    {
      id: 'projects',
      label: t('toolbar.projects'),
      icon: <FolderOpen />,
      onSelect: props.onOpenProjects,
    },
    { type: 'separator', id: 'sep-1' },
    { id: 'import', label: t('toolbar.import'), icon: <Upload />, onSelect: pickFile },
    { id: 'export', label: t('toolbar.export'), icon: <Download />, onSelect: props.onExport },
    { type: 'separator', id: 'sep-2' },
    {
      id: 'format',
      label: t('toolbar.format'),
      icon: <Wand />,
      shortcut: 'Shift+Alt+F',
      onSelect: props.onFormat,
    },
    { id: 'relayout', label: t('toolbar.relayout'), icon: <Layout />, onSelect: props.onRelayout },
    { id: 'swap', label: t('panel.swapPanels'), icon: <Columns />, onSelect: props.onSwapPanels },
    { type: 'separator', id: 'sep-3' },
    {
      id: 'settings',
      label: t('toolbar.settings'),
      icon: <Settings />,
      onSelect: props.onOpenSettings,
    },
    { id: 'help', label: t('toolbar.help'), icon: <HelpCircle />, onSelect: props.onOpenHelp },
  ];

  return (
    <header
      className="fs-safe-top flex shrink-0 items-center gap-1 border-b border-border bg-surface px-2 py-1.5"
      aria-label={t('a11y.landmarkBanner')}
    >
      <span className="flex items-center gap-2 pr-1 pl-1">
        <Workflow className="size-5 text-accent" aria-hidden="true" />
        <span className="hidden text-sm font-semibold tracking-tight sm:inline">
          {BRANDING.name}
        </span>
      </span>

      <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />

      {/* Project name: a plain text input, because a click-to-edit affordance
          that only reveals itself on hover is invisible on touch. */}
      <label className="min-w-0 flex-1">
        <span className="fs-sr-only">{t('projects.nameLabel')}</span>
        <input
          type="text"
          value={props.projectName}
          onChange={(event) => props.onProjectNameChange(event.target.value)}
          onFocus={() => setEditingName(true)}
          onBlur={() => setEditingName(false)}
          placeholder={t('projects.untitled')}
          className={[
            'w-full min-w-0 rounded px-2 py-1 text-sm font-medium transition-colors',
            'placeholder:text-ink-faint',
            editingName ? 'bg-surface-sunken' : 'bg-transparent hover:bg-surface-sunken',
          ].join(' ')}
        />
      </label>

      <SaveIndicator status={props.saveStatus} t={t} />

      <nav className="flex items-center gap-0.5" aria-label={t('a11y.landmarkToolbar')}>
        <IconButton label={t('toolbar.new')} onClick={props.onNew} icon={<Plus />} />

        {!compact ? (
          <>
            <IconButton label={t('toolbar.undo')} onClick={props.onUndo} icon={<Undo />} />
            <IconButton label={t('toolbar.redo')} onClick={props.onRedo} icon={<Redo />} />
            <IconButton label={t('toolbar.format')} onClick={props.onFormat} icon={<Wand />} />
            <IconButton label={t('toolbar.export')} onClick={props.onExport} icon={<Download />} />
          </>
        ) : null}

        <IconButton
          label={t('toolbar.theme')}
          onClick={() => props.onThemeChange(nextTheme)}
          aria-live="polite"
          icon={<ThemeIcon />}
        />

        {!compact ? <PanelLayoutToggle {...props} /> : null}

        <Menu
          label={t('toolbar.more')}
          items={overflowItems}
          align="end"
          variant="ghost"
          triggerIcon={<MoreHorizontal />}
          hideChevron
        />
      </nav>

      <input
        ref={fileInputRef}
        type="file"
        accept={[...BRANDING.acceptedSourceExtensions, '.json'].join(',')}
        onChange={onFileChosen}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
      />
    </header>
  );
}

function PanelLayoutToggle({ panelLayout, onPanelLayoutChange, t }: ToolbarProps) {
  const options: readonly { value: PanelLayout; label: string; Icon: typeof Code }[] = [
    { value: 'code', label: t('panel.codeOnly'), Icon: Code },
    { value: 'split', label: t('panel.split'), Icon: Columns },
    { value: 'diagram', label: t('panel.diagramOnly'), Icon: Workflow },
  ];

  return (
    <div className="ml-1 hidden items-center gap-0.5 rounded-md bg-surface-sunken p-0.5 lg:flex">
      {options.map(({ value, label, Icon }) => (
        <Tooltip key={value} label={label}>
          <button
            type="button"
            aria-pressed={panelLayout === value}
            aria-label={label}
            onClick={() => onPanelLayoutChange(value)}
            className={[
              'grid size-8 place-items-center rounded transition-colors',
              panelLayout === value
                ? 'bg-surface text-ink shadow-sm'
                : 'text-ink-muted hover:text-ink',
            ].join(' ')}
          >
            <Icon className="size-4" />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

function SaveIndicator({ status, t }: { status: SaveStatus; t: Translator['t'] }) {
  if (status === 'idle') return null;
  const text =
    status === 'saving'
      ? t('projects.saving')
      : status === 'saved'
        ? t('projects.saved')
        : t('projects.saveError');

  return (
    <span
      className={[
        'hidden shrink-0 px-2 text-xs sm:inline',
        status === 'error' ? 'text-danger' : 'text-ink-faint',
      ].join(' ')}
      role="status"
    >
      {text}
    </span>
  );
}
